import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import StatusBadge from "./StatusBadge";

function round2(n) {
  return Math.round(n * 100) / 100;
}

export default function EditReservationModal({
  show,
  onHide,
  reservation,
  onSubmit,
  loading,
}) {
  const initialState = useMemo(() => {
    return {
      status: reservation?.status || reservation?.state || "En attente",
      commentaire: reservation?.commentaire || "",

      // promo
      promoType: "percent", // "percent" | "amount"
      promoValue: "",
      prixOverride: "", // si tu veux forcer un prix direct
    };
  }, [reservation]);

  const [statusValue, setStatusValue] = useState(initialState.status);
  const [commentaire, setCommentaire] = useState(initialState.commentaire);

  const [promoType, setPromoType] = useState(initialState.promoType);
  const [promoValue, setPromoValue] = useState(initialState.promoValue);
  const [prixOverride, setPrixOverride] = useState(initialState.prixOverride);

  useEffect(() => {
    setStatusValue(initialState.status);
    setCommentaire(initialState.commentaire);
    setPromoType(initialState.promoType);
    setPromoValue(initialState.promoValue);
    setPrixOverride(initialState.prixOverride);
  }, [initialState]);

  if (!reservation) return null;

  const basePrix = Number(reservation.prix || 0);

  const promoValueNum =
    promoValue !== "" ? Number(String(promoValue).replace(",", ".")) : null;

  const prixOverrideNum =
    prixOverride !== "" ? Number(String(prixOverride).replace(",", ".")) : null;

  let previewPrix = basePrix;

  if (
    prixOverrideNum !== null &&
    Number.isFinite(prixOverrideNum) &&
    prixOverrideNum >= 0
  ) {
    previewPrix = prixOverrideNum;
  } else if (
    promoValueNum !== null &&
    Number.isFinite(promoValueNum) &&
    promoValueNum >= 0
  ) {
    if (promoType === "percent") {
      const p = Math.min(promoValueNum, 100);
      previewPrix = basePrix * (1 - p / 100);
    } else {
      previewPrix = basePrix - promoValueNum;
    }
    previewPrix = Math.max(0, previewPrix);
  }

  previewPrix = round2(previewPrix);

  const handleSubmit = async (e) => {
    e.preventDefault();

    await onSubmit({
      id: reservation._id,
      email: reservation.email,
      reservationId: reservation.reservationId, // sert de "clé admin" côté backend chez toi

      status: statusValue,
      commentaire,

      // prix/promo
      prix: prixOverride !== "" ? prixOverride : undefined,
      promoType: prixOverride !== "" ? undefined : promoType,
      promoValue: prixOverride !== "" ? undefined : promoValue,
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      dialogClassName="admin-modal"
    >
      <Modal.Header closeButton className="admin-modal-header">
        <Modal.Title className="d-flex align-items-center gap-2">
          Modifier la réservation
          {reservation.reservationId ? (
            <span className="muted">#{reservation.reservationId}</span>
          ) : null}
        </Modal.Title>
        <div className="d-flex align-items-center gap-2">
          <StatusBadge status={reservation.status || reservation.state} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <div className="admin-kpi-card">
              <div className="admin-kpi-title">Client</div>
              <div className="admin-kpi-value">
                {reservation.firstname || reservation.prenom}{" "}
                {reservation.name || reservation.nom}
              </div>
              <div className="admin-kpi-sub">
                {reservation.email} • {reservation.phone}
              </div>
            </div>
          </Col>

          <Col md={6}>
            <div className="admin-kpi-card">
              <div className="admin-kpi-title">Course</div>
              <div className="admin-kpi-value">
                {reservation.date} à {reservation.heure}
              </div>
              <div className="admin-kpi-sub">
                {(reservation.depart || reservation.adresseDepart) ?? "—"} →{" "}
                {(reservation.arrivee || reservation.adresseArrive) ?? "—"}
              </div>
              <div className="admin-kpi-sub">
                {reservation.serviceType} • <strong>{basePrix} €</strong>
              </div>
            </div>
          </Col>
        </Row>

        <Form onSubmit={handleSubmit} className="mt-3">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Nouveau statut</Form.Label>
                <Form.Select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  required
                >
                  <option value="En attente">En attente</option>
                  <option value="confirmée">Confirmée</option>
                  <option value="annulée">Annulée</option>
                  <option value="terminée">Terminée</option>
                  <option value="refusée">Refusée</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Statut actuel</Form.Label>
                <Form.Control
                  value={reservation.status || reservation.state || ""}
                  readOnly
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <div className="admin-kpi-card">
                <div className="admin-kpi-title">Tarification</div>

                <Row className="g-3 mt-1">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Prix actuel</Form.Label>
                      <InputGroup>
                        <Form.Control value={basePrix} readOnly />
                        <InputGroup.Text>€</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Calculé / enregistré actuellement.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Forcer un prix</Form.Label>
                      <InputGroup>
                        <Form.Control
                          value={prixOverride}
                          onChange={(e) => setPrixOverride(e.target.value)}
                          placeholder="Ex: 55"
                          inputMode="decimal"
                        />
                        <InputGroup.Text>€</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Si renseigné, ignore la promo.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Promo</Form.Label>
                      <InputGroup>
                        <Form.Select
                          value={promoType}
                          onChange={(e) => setPromoType(e.target.value)}
                          disabled={prixOverride !== ""}
                        >
                          <option value="percent">%</option>
                          <option value="amount">€</option>
                        </Form.Select>
                        <Form.Control
                          value={promoValue}
                          onChange={(e) => setPromoValue(e.target.value)}
                          placeholder={
                            promoType === "percent" ? "Ex: 10" : "Ex: 5"
                          }
                          disabled={prixOverride !== ""}
                          inputMode="decimal"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Réduction appliquée sur le prix actuel.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <div className="admin-price-preview">
                      <span>Prix final (prévisualisation)</span>
                      <strong>{previewPrix} €</strong>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>Commentaire (admin / client)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  placeholder="Ex: Promo appliquée, réservation confirmée. Merci."
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="admin-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
          Annuler
        </Button>
        <Button
          className="btn-primary-gold"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Modification..." : "Enregistrer"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
